import React, { useState } from 'react';
import { MoreVertical, Edit, Trash2, Share2, Clock, Copy, Check, Save, History } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import EditPromptModal from './EditPromptModal';
import SharePromptModal from './SharePromptModal';
import ReminderModal from './ReminderModal';
import VersionHistoryModal from './VersionHistoryModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function PromptCard({ prompt, onUpdate }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSaveAsTemplate = async () => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/api/user-templates`,
        {
          title: prompt.title,
          content: prompt.content,
          function_tag: prompt.function_tag,
          category_tag: prompt.category_tag,
          model_tag: prompt.model_tag,
          commentary: prompt.commentary
        },
        { withCredentials: true, headers }
      );
      toast.success('Saved as template');
      setShowMenu(false);
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleCopy = async () => {
    navigator.clipboard.writeText(prompt.content);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
    
    // Track analytics
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/analytics/track?prompt_id=${prompt.prompt_id}&action=copy`,
        {},
        { withCredentials: true, headers }
      );
    } catch (error) {
      // Silent fail
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this prompt?')) return;

    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API_URL}/api/prompts/${prompt.prompt_id}`, {
        withCredentials: true,
        headers
      });
      toast.success('Prompt deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete prompt');
    }
  };

  const getFunctionTagColor = (tag) => {
    const colors = {
      'Sales': 'bg-[#EAF0EC] text-[#2C4C3B]',
      'Marketing': 'bg-[#F5F2EA] text-[#78716C]',
      'Customer Success': 'bg-[#EAF0EC] text-[#2C4C3B]',
    };
    return colors[tag] || 'bg-[#F5F2EA] text-[#78716C]';
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm overflow-hidden flex flex-col p-4 transition-colors hover:border-[#2C4C3B]" data-testid={`prompt-card-${prompt.prompt_id}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg tracking-tight font-medium text-[#1C1917] flex-1" data-testid="prompt-title">{prompt.title}</h3>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="text-[#78716C] hover:text-[#1C1917] transition-colors"
              data-testid="prompt-menu-button"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-6 bg-white border border-[#E7E5E4] rounded-lg shadow-lg p-1 z-20 min-w-[140px]">
                  <button
                    onClick={() => { setShowEditModal(true); setShowMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#FAFAF9] rounded flex items-center gap-2"
                    data-testid="edit-prompt-button"
                  >
                    <Edit size={14} /> Edit
                  </button>
                  <button
                    onClick={() => { setShowShareModal(true); setShowMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#FAFAF9] rounded flex items-center gap-2"
                    data-testid="share-prompt-button"
                  >
                    <Share2 size={14} /> Share
                  </button>
                  <button
                    onClick={() => { setShowReminderModal(true); setShowMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#FAFAF9] rounded flex items-center gap-2"
                    data-testid="reminder-prompt-button"
                  >
                    <Clock size={14} /> Reminder
                  </button>
                  <button
                    onClick={() => { setShowVersionHistory(true); setShowMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#FAFAF9] rounded flex items-center gap-2"
                    data-testid="version-history-button"
                  >
                    <History size={14} /> History
                  </button>
                  <button
                    onClick={handleSaveAsTemplate}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#FAFAF9] rounded flex items-center gap-2"
                    data-testid="save-template-button"
                  >
                    <Save size={14} /> Save as Template
                  </button>
                  <button
                    onClick={handleCopy}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#FAFAF9] rounded flex items-center gap-2"
                    data-testid="copy-prompt-button"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDelete}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600 rounded flex items-center gap-2"
                    data-testid="delete-prompt-button"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {prompt.function_tag && (
            <span className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase ${getFunctionTagColor(prompt.function_tag)}`} data-testid="function-tag">
              {prompt.function_tag}
            </span>
          )}
          {prompt.category_tag && (
            <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#F5F2EA] text-[#78716C]" data-testid="category-tag">
              {prompt.category_tag}
            </span>
          )}
          {prompt.model_tag && (
            <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#EAF0EC] text-[#2C4C3B]" data-testid="model-tag">
              {prompt.model_tag}
            </span>
          )}
        </div>

        {/* Content */}
        <p className="text-sm leading-relaxed text-[#57534E] mb-3 line-clamp-3" data-testid="prompt-content">
          {prompt.content}
        </p>

        {/* Commentary */}
        {prompt.commentary && (
          <p className="text-xs text-[#78716C] italic" data-testid="prompt-commentary">
            {prompt.commentary}
          </p>
        )}
      </div>

      {showEditModal && (
        <EditPromptModal
          prompt={prompt}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            onUpdate();
          }}
        />
      )}

      {showShareModal && (
        <SharePromptModal
          prompt={prompt}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showReminderModal && (
        <ReminderModal
          prompt={prompt}
          onClose={() => setShowReminderModal(false)}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryModal
          prompt={prompt}
          onClose={() => setShowVersionHistory(false)}
          onRestore={() => {
            setShowVersionHistory(false);
            onUpdate();
          }}
        />
      )}
    </>
  );
}